import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { calibrate_scale_defect, calibrate_scale_defect_factory, updateHubName } from '../../../api/api_hubs';
import { unassignPot } from '../../../api/api_pots';
import { fetchPlant } from '../../../api/api_plants';
import { fetchCustomPlant } from '../../../api/api_customPlants';
import { ResponsiveTitle } from '../../Entities/ResponsiveText';

const EditHub = () => {
    const { translations } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const { hub } = location.state;

    const [hubName, setHubName] = useState(hub.name);
    const [pots, setPots] = useState(Array.isArray(hub.pots) ? hub.pots : []);
    const [plantsInfo, setPlantsInfo] = useState({});
    const [isScalePopupOpen, setIsScalePopupOpen] = useState(false);

    useEffect(() => {
        const updatedPlantsInfo = {};
        const potPromises = pots.map(pot => {
            const plantIds = pot.plant_ids ? pot.plant_ids.split(',').filter(Boolean) : [];
            const customPlantIds = pot.custom_plant_ids ? pot.custom_plant_ids.split(',').filter(Boolean) : [];

            return Promise.all([
                ...plantIds.map(async (plantId) => {
                    const response = await fetchPlant(plantId);
                    return response.error ? { name: 'Unknown Plant' } : response.data;
                }),
                ...customPlantIds.map(async (customPlantId) => {
                    const response = await fetchCustomPlant(customPlantId);
                    return response.error ? { name: 'Unknown Plant' } : response.data;
                })
            ]).then(results => {
                updatedPlantsInfo[pot.id] = {
                    ...updatedPlantsInfo[pot.id],
                    plants: results.map(plant => plant.name)
                };
            });
        });

        Promise.all(potPromises).then(() => {
            setPlantsInfo(updatedPlantsInfo);
        }).catch(error => {
            console.error('Error updating plants info:', error);
        });
    }, [pots]);

    const handleSave = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const response = await updateHubName(hub.id, hubName, token);
            if (response && !response.error) {
                alert(translations.hub_updated_successfully);
                navigate(-1);
            } else {
                alert(translations.failed_to_update_hub);
            }
        } catch (error) {
            console.error('Error updating hub:', error);
            alert('An error occurred while updating the hub.');
        }
    };

    const handleDeletePot = async (potId, hubSN, potSN) => {
        if (window.confirm(translations.confirm_delete_pot)) {
            const token = localStorage.getItem('userToken');
            const response = await unassignPot(hubSN, potId, potSN, token);
            if (response && !response.error) {
                setPots(prevPots => prevPots.filter(pot => pot.id !== potId));
            } else {
                alert(translations.failed_to_delete_pot);
            }
        }
    };

    const handleEditPot = (hubSN, pot, irrigationSystemEnabled) => {
        navigate(`editPot/${pot.pot_sensor_device_MAC}`, {
            state: {
                hubSN,
                pot,
                irrigation_system_enabled: irrigationSystemEnabled
            }
        });
    };

    const toggleScalePopup = () => setIsScalePopupOpen(prev => !prev);

    const handleBack = () => {
        navigate(-1);
    };

    const handleCalibrateScale = () => {
        const token = localStorage.getItem('userToken');
        calibrate_scale_defect(hub, token);
        toggleScalePopup();
    };

    const handleCalibrateScaleFactory = () => {
        const token = localStorage.getItem('userToken');
        calibrate_scale_defect_factory(hub, token);
        toggleScalePopup();
    };

    return (
        <div className="sm-page">
            <div className="sm-container">
                <div className="header">
                    <button onClick={handleBack} className="back-button">&#8592;</button>
                    <h1>{translations.edit_hub}</h1>
                </div>
                <div className="content">
                    <span>{translations.name}</span>
                    <input
                        type="text"
                        className="sm-input"
                        value={hubName}
                        onChange={(e) => setHubName(e.target.value)}
                    />
                    {pots && (
                        <ul className="sm-hub-list">
                            <div className="sm-space-info">
                                {pots.map(pot => (
                                    <li key={pot.id} className="sm-space-container">
                                        <div className="sm-space-header">
                                            <ResponsiveTitle
                                                text={pot.name}
                                                style={{ flex: 1 }}
                                                className="sm-space-info-title"
                                            />
                                            <button
                                                onClick={() =>
                                                    handleEditPot(hub.SN, pot, hub.irrigation_system_enabled)
                                                }
                                                className="general-button edit-button sm-button"
                                            >
                                                <i className="fi fi-rr-pencil"></i>
                                            </button>
                                            <button
                                                onClick={() => handleDeletePot(pot.id, hub.SN, pot.SN)}
                                                className="general-button delete-button sm-button"
                                            >
                                                <i className="fi fi-rr-trash"></i>
                                            </button>
                                        </div>
                                        <div>
                                            <span>{translations.plants}:</span>
                                            <ul style={{ listStyleType: 'none' }}>
                                                {plantsInfo[pot.id]?.plants?.map((plantName, index) => (
                                                    <li key={index}>{plantName}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </li>
                                ))}
                            </div>
                        </ul>
                    )}
                </div>

                <button onClick={toggleScalePopup} className="general-button edit-button">
                    {translations.calibrate_scale_defect}
                </button>

                {isScalePopupOpen && (
                    <div className="info-popup">
                        <div className="info-popup-content">
                            <p>{translations.confirm_water_level_zero}</p>
                            <button onClick={handleCalibrateScale} className="btn-scale-confirm">
                                {translations.confirm}
                            </button>
                            <button
                                onClick={handleCalibrateScaleFactory}
                                className="btn-scale-reset"
                            >
                                {translations.reset_calibrate_scale_defect}
                            </button>
                            <button onClick={toggleScalePopup} className="btn-scale-cancel">
                                {translations.cancel}
                            </button>
                        </div>
                    </div>
                )}

                <div className="content">
                    <button onClick={handleSave} className="general-button">
                        {translations.save}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditHub;
